<!--
# -----------------------------------------------------------------------------
# Component: PolicyRiskTrailer.vue
# Author: PST
# Created Date: 13/Mar/2020
# Comments: Policy Risk -> Risk Trailer Tab
# -----------------------------------------------------------------------------
-->
<template>
  <div>
    <v-container fluid grid-list-lg>
      <v-layout row wrap>
        <v-flex xs12>
          <v-toolbar dense color="grey lighten-5" flat>
            <v-icon>mdi-format-align-justify</v-icon>
            <v-toolbar-title class="toolbar-title-2">{{ labels.mainTitle }}</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-container fluid grid-list-sm>
              <v-layout row wrap>
                <v-flex xs12>
                  <v-data-table :headers="headers" :items="itemList" class="elevation-1" :loading="loading">
                    <template v-slot:item="row">
                      <tr>
                        <td>{{ row.item.riskItem }}</td>
                        <td>{{ row.item.busCls }}</td>
                        <td>{{ row.item.dNAP }}</td>
                        <td>{{ row.item.premDue }}</td>
                        <td>{{ row.item.disc }}</td>
                        <td>{{ row.item.comm1 }}</td>
                        <td>{{ row.item.comm2 }}</td>
                        <td>{{ row.item.comm3 }}</td>
                        <td>{{ row.item.stamp }}</td>
                        <td>{{ row.item.fee }}</td>
                        <td>{{ row.item.tax }}</td>
                        <td>{{ row.item.applyGst }}</td>
                        <td style="text-align: center">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <v-btn icon v-on="on" color="primary" text @click.prevent="doSelectItem(row.item)" :loading="policy.saving">
                                <v-icon>mdi-pencil</v-icon>
                              </v-btn>
                            </template>
                            <span>{{ labels.edit }}</span>
                          </v-tooltip>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-flex>
                <v-flex xs12>
                  <v-card v-show="showExpandRiskTrailer">
                    <v-toolbar dense color="grey lighten-5" flat>
                      <v-icon>mdi-format-align-justify</v-icon>
                      <v-toolbar-title class="toolbar-title-3">Risk {{ labels.riskItem }} Summary</v-toolbar-title>
                      <v-spacer></v-spacer>
                      <v-btn text icon @click="showExpandRiskTrailer = false">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-toolbar>
                    <v-card-text>
                      <v-container fluid grid-list-lg>
                        <v-layout row wrap>
                          <v-flex xs3>
                            <v-text-field v-model="mainForm.acctNo1" :label="labels.acctNo1" disabled></v-text-field>
                          </v-flex>
                          <v-flex xs1> </v-flex>
                          <v-flex xs5>
                            <v-text-field v-model="mainForm.acctNoSfx" :label="labels.acctNoSfx" disabled></v-text-field>
                          </v-flex>
                          <v-flex xs1> </v-flex>
                          <v-flex xs2>
                            <v-text-field v-model="mainForm.billCurr" :label="labels.billCurr" disabled></v-text-field>
                          </v-flex>

                          <v-flex xs4> </v-flex>
                          <v-flex xs2>
                            <v-text-field v-model="mainForm.nap" :label="labels.nap" disabled></v-text-field>
                          </v-flex>
                          <v-flex xs1> </v-flex>
                          <v-flex xs2>
                            <v-text-field v-model="mainForm.premDue" :label="labels.premDue" disabled></v-text-field>
                          </v-flex>
                          <v-flex xs1> </v-flex>
                          <v-flex xs2>
                            <v-text-field v-model="mainForm.applyGst" :label="labels.applyGst" disabled></v-text-field>
                          </v-flex>

                          <v-flex xs3> </v-flex>
                          <v-flex xs1>
                            <v-checkbox class="align-end justify-end" color="primary" v-model="mainForm.discPctAe" @change="doDiscPct"></v-checkbox>
                          </v-flex>
                          <v-flex xs2>
                            <v-text-field
                              ref="discPct"
                              v-model="mainForm.discPct"
                              :label="labels.discPct"
                              :disabled="mainForm.discPctAe"
                              @blur="doDisc"
                              v-num-mask="{ negative: true, intScale: 3, decScale: 4 }"
                            ></v-text-field>
                          </v-flex>

                          <v-flex xs1>
                            <v-checkbox class="align-end justify-end" color="primary" v-model="mainForm.discAe" @change="doDisc"></v-checkbox>
                          </v-flex>
                          <v-flex xs2>
                            <v-text-field
                              ref="disc"
                              v-model="mainForm.disc"
                              :label="labels.disc"
                              :disabled="mainForm.discAe"
                              @blur="doPremGST"
                              v-num-mask="{ negative: true, intScale: 18, decScale: 2 }"
                            ></v-text-field>
                          </v-flex>
                          <v-flex xs1> </v-flex>
                          <v-flex xs2>
                            <v-text-field v-model="mainForm.premGst" :label="labels.premGst" disabled></v-text-field>
                          </v-flex>

                          <v-flex xs3>
                            <v-autocomplete
                              :label="labels.redBal1"
                              :items="yesnoList"
                              v-model="mainForm.redBal1"
                              item-text="label"
                              item-value="value"
                              data-vv-name="redBal1"
                              :data-vv-as="labels.redBal1"
                              :error-messages="errors.collect('redBal1')"
                              v-validate="'required'"
                              :disabled="!(mainForm.riskGrpNo === 1 && mainForm.riskNo === 1)"
                              @change="doComm1pctAe"
                            ></v-autocomplete>
                          </v-flex>
                          <v-flex xs1>
                            <v-checkbox
                              class="align-end justify-end"
                              color="primary"
                              v-model="mainForm.comm1pctAe"
                              @change="doComm1pctAe"
                            ></v-checkbox>
                          </v-flex>
                          <v-flex xs2>
                            <v-text-field
                              ref="comm1pct"
                              v-model="mainForm.comm1pct"
                              :label="labels.comm1pct"
                              :disabled="mainForm.comm1pctAe"
                              @blur="doComm1"
                              v-num-mask="{ negative: true, intScale: 3, decScale: 4 }"
                            ></v-text-field>
                          </v-flex>
                          <v-flex xs1>
                            <v-checkbox class="align-end justify-end" color="primary" v-model="mainForm.comm1ae" @change="doComm1"></v-checkbox>
                          </v-flex>
                          <v-flex xs2>
                            <v-text-field
                              ref="comm1"
                              v-model="mainForm.comm1"
                              :label="labels.comm1"
                              :disabled="mainForm.comm1ae"
                              @blur="doTotAcctNo1"
                              v-num-mask="{ negative: true, intScale: 18, decScale: 2 }"
                            ></v-text-field>
                          </v-flex>
                          <v-flex xs1> </v-flex>
                          <v-flex xs2>
                            <!-- <v-text-field v-model="mainForm.comm1gst" :label="labels.comm1gst" disabled></v-text-field> -->
                          </v-flex>

                          <v-flex xs3> </v-flex>
                          <v-flex xs1>
                            <v-checkbox
                              class="align-end justify-end"
                              color="primary"
                              v-model="mainForm.stampAe"
                              @change="doStampAe"
                              :disabled="!perRiskStp"
                            ></v-checkbox>
                          </v-flex>
                          <v-flex xs2>
                            <v-text-field
                              ref="stamp"
                              v-model="mainForm.stamp"
                              :label="labels.stamp"
                              :disabled="mainForm.stampAe"
                              @blur="doTotAcctNo1"
                              v-num-mask="{ negative: true, intScale: 18, decScale: 2 }"
                              :loading="stampLoading"
                            ></v-text-field>
                          </v-flex>
                          <v-flex xs1>
                            <v-checkbox
                              class="align-end justify-end"
                              color="primary"
                              v-model="mainForm.feeAe"
                              @change="doFeeAe"
                              :disabled="!perRiskFee"
                            ></v-checkbox>
                          </v-flex>
                          <v-flex xs2>
                            <v-text-field
                              ref="fee"
                              v-model="mainForm.fee"
                              :label="labels.fee"
                              :disabled="mainForm.feeAe"
                              @blur="doTotAcctNo1"
                              v-num-mask="{ negative: true, intScale: 18, decScale: 2 }"
                              :loading="feeLoading"
                            ></v-text-field>
                          </v-flex>
                          <v-flex xs1>
                            <v-checkbox
                              class="align-end justify-end"
                              color="primary"
                              v-model="mainForm.taxAe"
                              @change="doTaxAe"
                              :disabled="!perRiskTax"
                            ></v-checkbox>
                          </v-flex>
                          <v-flex xs2>
                            <v-text-field
                              ref="tax"
                              v-model="mainForm.tax"
                              :label="labels.tax"
                              :disabled="mainForm.taxAe"
                              @blur="doTotAcctNo1"
                              v-num-mask="{ negative: true, intScale: 18, decScale: 2 }"
                              :loading="taxLoading"
                            ></v-text-field>
                          </v-flex>

                          <v-flex xs3> </v-flex>
                          <v-flex xs1>
                            <v-checkbox class="align-end justify-end" color="primary" v-model="polMaster.polStampAE" disabled></v-checkbox>
                          </v-flex>
                          <v-flex xs2>
                            <v-text-field v-model="mainForm.polStamp" :label="labels.polStamp" disabled></v-text-field>
                          </v-flex>
                          <v-flex xs1>
                            <v-checkbox class="align-end justify-end" color="primary" v-model="polMaster.polFeeAE" disabled></v-checkbox>
                          </v-flex>
                          <v-flex xs2>
                            <v-text-field v-model="mainForm.polFee" :label="labels.polFee" disabled></v-text-field>
                          </v-flex>
                          <v-flex xs1>
                            <v-checkbox class="align-end justify-end" color="primary" v-model="polMaster.polTaxAE" disabled></v-checkbox>
                          </v-flex>
                          <v-flex xs2>
                            <v-text-field v-model="mainForm.polTax" :label="labels.polTax" disabled></v-text-field>
                          </v-flex>

                          <v-flex xs3> </v-flex>
                          <v-flex xs1> </v-flex>
                          <v-flex xs2>
                            <v-text-field v-model="mainForm.totAcctNo1" :label="labels.totAcctNo1" disabled></v-text-field>
                          </v-flex>
                          <v-flex xs1> </v-flex>
                          <v-flex xs2>
                            <!-- <v-checkbox v-model="mainForm.applyGST" :label="labels.applyGST" data-vv-name="applyGST" :data-vv-as="labels.applyGST" :error-messages="errors.collect('applyGST')" hide-details></v-checkbox> -->
                          </v-flex>
                          <v-flex xs1> </v-flex>
                          <v-flex xs2> </v-flex>

                          <v-flex xs12>
                            <v-card>
                              <v-expansion-panels>
                                <v-expansion-panel>
                                  <v-expansion-panel-header>
                                    <div>
                                      <v-icon>mdi-cash</v-icon><span class="headerTitle">{{ labels.otherBilling }}</span>
                                    </div>
                                  </v-expansion-panel-header>
                                  <v-expansion-panel-content>
                                    <v-card>
                                      <v-card-text>
                                        <v-container fluid grid-list-lg>
                                          <v-layout row wrap>
                                            <v-flex xs3>
                                              <v-autocomplete
                                                :items="interList"
                                                v-model="mainForm.acctNo2"
                                                :label="labels.acctNo2"
                                                item-text="label"
                                                item-value="value"
                                                :disabled="!(mainForm.riskGrpNo === 1 && mainForm.riskNo === 1)"
                                                :loading="interLoading"
                                                clearable
                                                @change="onValueChange"
                                              ></v-autocomplete>
                                            </v-flex>
                                            <v-flex xs1> </v-flex>
                                            <v-flex xs5>
                                              <v-text-field v-model="mainForm.acctNo2Sfx" :label="labels.acctNoSfx" disabled></v-text-field>
                                            </v-flex>
                                            <v-flex xs3> </v-flex>

                                            <v-flex xs3>
                                              <v-autocomplete
                                                :label="labels.redBal2"
                                                :items="yesnoList"
                                                v-model="mainForm.redBal2"
                                                item-text="label"
                                                item-value="value"
                                                data-vv-name="redBal2"
                                                :data-vv-as="labels.redBal2"
                                                :error-messages="errors.collect('redBal2')"
                                                v-validate="'required'"
                                                :disabled="!(mainForm.riskGrpNo === 1 && mainForm.riskNo === 1)"
                                                @change="doComm2pctAe"
                                              ></v-autocomplete>
                                            </v-flex>
                                            <v-flex xs1>
                                              <v-checkbox
                                                class="align-end justify-end"
                                                color="primary"
                                                v-model="mainForm.comm2pctAe"
                                                @change="doComm2pctAe"
                                              ></v-checkbox>
                                            </v-flex>
                                            <v-flex xs2>
                                              <v-text-field
                                                ref="comm2pct"
                                                v-model="mainForm.comm2pct"
                                                :label="labels.comm2pct"
                                                :disabled="mainForm.comm2pctAe"
                                                @blur="doComm2"
                                                v-num-mask="{ negative: true, intScale: 3, decScale: 4 }"
                                              ></v-text-field>
                                            </v-flex>
                                            <v-flex xs1>
                                              <v-checkbox
                                                class="align-end justify-end"
                                                color="primary"
                                                v-model="mainForm.comm2ae"
                                                @change="doComm2"
                                              ></v-checkbox>
                                            </v-flex>
                                            <v-flex xs2>
                                              <v-text-field
                                                ref="comm2"
                                                v-model="mainForm.comm2"
                                                :label="labels.comm2"
                                                :disabled="mainForm.comm2ae"
                                                @blur="doTotAcctNo2"
                                                v-num-mask="{ negative: true, intScale: 18, decScale: 2 }"
                                              ></v-text-field>
                                            </v-flex>
                                            <v-flex xs1> </v-flex>
                                            <v-flex xs2>
                                              <!-- <v-text-field v-model="mainForm.comm2gst" :label="labels.comm2gst" disabled></v-text-field> -->
                                            </v-flex>

                                            <v-flex xs3> </v-flex>
                                            <v-flex xs1> </v-flex>
                                            <v-flex xs2>
                                              <v-text-field v-model="mainForm.totAcctNo2" :label="labels.totAcctNo2" disabled></v-text-field>
                                            </v-flex>
                                            <v-flex xs1> </v-flex>
                                            <v-flex xs2> </v-flex>
                                            <v-flex xs1> </v-flex>
                                            <v-flex xs2> </v-flex>

                                            <v-flex xs12>
                                              <v-divider></v-divider>
                                            </v-flex>

                                            <v-flex xs3>
                                              <v-autocomplete
                                                :items="interList"
                                                v-model="mainForm.acctNo3"
                                                :label="labels.acctNo3"
                                                item-text="label"
                                                item-value="value"
                                                :disabled="!(mainForm.riskGrpNo === 1 && mainForm.riskNo === 1)"
                                                :loading="interLoading"
                                                clearable
                                                @change="onValueChangeComm3"
                                              ></v-autocomplete>
                                            </v-flex>
                                            <v-flex xs1> </v-flex>
                                            <v-flex xs5>
                                              <v-text-field v-model="mainForm.acctNo3Sfx" :label="labels.acctNoSfx" disabled></v-text-field>
                                            </v-flex>
                                            <v-flex xs3> </v-flex>

                                            <v-flex xs3>
                                              <v-autocomplete
                                                :label="labels.redBal3"
                                                :items="yesnoList"
                                                v-model="mainForm.redBal3"
                                                item-text="label"
                                                item-value="value"
                                                data-vv-name="redBal3"
                                                :data-vv-as="labels.redBal2"
                                                :error-messages="errors.collect('redBal3')"
                                                v-validate="'required'"
                                                :disabled="!(mainForm.riskGrpNo === 1 && mainForm.riskNo === 1)"
                                                @change="doComm3pctAe"
                                              ></v-autocomplete>
                                            </v-flex>
                                            <v-flex xs1>
                                              <v-checkbox
                                                class="align-end justify-end"
                                                color="primary"
                                                v-model="mainForm.comm3pctAe"
                                                @change="doComm3pctAe"
                                              ></v-checkbox>
                                            </v-flex>
                                            <v-flex xs2>
                                              <v-text-field
                                                ref="comm3pct"
                                                v-model="mainForm.comm3pct"
                                                :label="labels.comm3pct"
                                                :disabled="mainForm.comm3pctAe"
                                                @blur="doComm3"
                                                v-num-mask="{ negative: true, intScale: 3, decScale: 4 }"
                                              ></v-text-field>
                                            </v-flex>
                                            <v-flex xs1>
                                              <v-checkbox
                                                class="align-end justify-end"
                                                color="primary"
                                                v-model="mainForm.comm3ae"
                                                @change="doComm3"
                                              ></v-checkbox>
                                            </v-flex>
                                            <v-flex xs2>
                                              <v-text-field
                                                ref="comm3"
                                                v-model="mainForm.comm3"
                                                :label="labels.comm3"
                                                :disabled="mainForm.comm3ae"
                                                @blur="doTotAcctNo3"
                                                v-num-mask="{ negative: true, intScale: 18, decScale: 2 }"
                                              ></v-text-field>
                                            </v-flex>
                                            <v-flex xs1> </v-flex>
                                            <v-flex xs2>
                                              <!-- <v-text-field v-model="mainForm.comm3gst" :label="labels.comm3gst" disabled></v-text-field> -->
                                            </v-flex>

                                            <v-flex xs3> </v-flex>
                                            <v-flex xs1> </v-flex>
                                            <v-flex xs2>
                                              <v-text-field v-model="mainForm.totAcctNo3" :label="labels.totAcctNo3" disabled></v-text-field>
                                            </v-flex>
                                            <v-flex xs1> </v-flex>
                                            <v-flex xs2> </v-flex>
                                            <v-flex xs1> </v-flex>
                                            <v-flex xs2> </v-flex>
                                          </v-layout>
                                        </v-container>
                                      </v-card-text>
                                    </v-card>
                                  </v-expansion-panel-content>
                                </v-expansion-panel>
                                <v-expansion-panel>
                                  <v-expansion-panel-header>
                                    <div>
                                      <v-icon>mdi-cash</v-icon><span class="headerTitle">{{ labels.coRiSummary }}</span>
                                    </div>
                                  </v-expansion-panel-header>
                                  <v-expansion-panel-content>
                                    <v-card>
                                      <v-card-text>
                                        <v-container fluid grid-list-lg>
                                          <v-layout row wrap>
                                            <v-flex xs4> </v-flex>
                                            <v-flex xs2>
                                              <v-subheader class="queryLabel" :loading="coRiLoading">{{ labels.si }}</v-subheader>
                                            </v-flex>
                                            <v-flex xs2>
                                              <v-subheader class="queryLabel" :loading="coRiLoading">{{ labels.sharePct }}</v-subheader>
                                            </v-flex>
                                            <v-flex xs4> </v-flex>

                                            <v-flex xs4>
                                              <v-subheader class="queryLabel">{{ labels.coIns }}</v-subheader>
                                            </v-flex>
                                            <v-flex xs2>
                                              <div class="queryValue">{{ riForm.dCoinsSI }}</div>
                                            </v-flex>
                                            <v-flex xs2>
                                              <div class="queryValue">{{ riForm.dCoinsSIPct }}</div>
                                            </v-flex>
                                            <v-flex xs4> </v-flex>

                                            <!-- gross -->
                                            <v-flex xs4>
                                              <v-subheader class="queryLabel">{{ labels.gross }}</v-subheader>
                                            </v-flex>
                                            <v-flex xs2>
                                              <div class="queryValue">{{ riForm.dSI }}</div>
                                            </v-flex>
                                            <v-flex xs2> </v-flex>
                                            <v-flex xs4> </v-flex>

                                            <!-- stats -->
                                            <v-flex xs4>
                                              <v-subheader class="queryLabel">{{ labels.stats }}</v-subheader>
                                            </v-flex>
                                            <v-flex xs2>
                                              <div class="queryValue">{{ riForm.dStatSI }}</div>
                                            </v-flex>
                                            <v-flex xs2>
                                              <div class="queryValue">{{ riForm.dStatSIPct }}</div>
                                            </v-flex>
                                            <v-flex xs4> </v-flex>

                                            <!-- fac -->
                                            <v-flex xs4>
                                              <v-subheader class="queryLabel">{{ labels.fac }}</v-subheader>
                                            </v-flex>
                                            <v-flex xs2>
                                              <div class="queryValue">{{ riForm.dFacSI }}</div>
                                            </v-flex>
                                            <v-flex xs2>
                                              <div class="queryValue">{{ riForm.dFacSIPct }}</div>
                                            </v-flex>
                                            <v-flex xs4> </v-flex>

                                            <!-- surplus -->
                                            <v-flex xs4>
                                              <v-subheader class="queryLabel">{{ labels.surplus }}</v-subheader>
                                            </v-flex>
                                            <v-flex xs2>
                                              <div class="queryValue">{{ riForm.dTtySI }}</div>
                                            </v-flex>
                                            <v-flex xs2>
                                              <div class="queryValue">{{ riForm.dTtySIPct }}</div>
                                            </v-flex>
                                            <v-flex xs4> </v-flex>

                                            <!-- net -->
                                            <v-flex xs4>
                                              <v-subheader class="queryLabel">{{ labels.net }}</v-subheader>
                                            </v-flex>
                                            <v-flex xs2>
                                              <div class="queryValue">{{ riForm.dNetSI }}</div>
                                            </v-flex>
                                            <v-flex xs2>
                                              <div class="queryValue">{{ riForm.dNetSIPct }}</div>
                                            </v-flex>
                                            <v-flex xs4> </v-flex>

                                            <!-- qs -->
                                            <v-flex xs4>
                                              <v-subheader class="queryLabel">{{ labels.qs }}</v-subheader>
                                            </v-flex>
                                            <v-flex xs2>
                                              <div class="queryValue">{{ riForm.dOthSI }}</div>
                                            </v-flex>
                                            <v-flex xs2>
                                              <div class="queryValue">{{ riForm.dOthSIPct }}</div>
                                            </v-flex>
                                            <v-flex xs4> </v-flex>
                                          </v-layout>
                                        </v-container>
                                      </v-card-text>
                                    </v-card>
                                  </v-expansion-panel-content>
                                </v-expansion-panel>
                              </v-expansion-panels>
                            </v-card>
                          </v-flex>
                        </v-layout>
                      </v-container>
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn depressed @click.prevent="showExpandRiskTrailer = false" :loading="policy.saving"
                        ><span class="btn-normal-error">Close</span></v-btn
                      >
                      <v-btn depressed type="submit" @click.prevent="doSaveRiskTrailer" :loading="policy.saving"
                        ><span class="btn-normal-primary">{{ labels.save }}</span></v-btn
                      >
                    </v-card-actions>
                  </v-card>
                </v-flex>
                <v-flex xs12>
                  <v-btn class="mr-2" color="error" dark :loading="policy.saving" @click.prevent="doExit">
                    {{ labels.exit }}
                    <v-icon dark right>mdi-exit-to-app</v-icon>
                  </v-btn>
                  <v-btn class="mr-2" color="primary" dark :loading="policy.saving" @click.prevent="doProceed">
                    {{ labels.proceed }}
                    <v-icon dark right>mdi-arrow-right-bold-box</v-icon>
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>
        </v-flex>
      </v-layout>
    </v-container>

    <v-dialog v-model="policy.saving" persistent width="300">
      <v-card color="primary" dark>
        <v-card-text class="pa-3">
          Processing...
          <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showErrorDialog" width="800px">
      <v-card>
        <v-toolbar dense color="grey lighten-3" flat>
          <v-icon>mdi-information</v-icon>
          <v-toolbar-title>System Information</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text>
          <div v-if="messages.length > 0" class="pb-3">
            <v-subheader>Oops something went wrong:</v-subheader>
            <ul class="showErrorMsgs">
              <li v-for="(error, index) in messages" :key="index">
                {{ error.message }}
              </li>
            </ul>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" text @click.native="showErrorDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showCommDialog" width="800px">
      <v-card>
        <v-card-title primary-title>
          <div>
              <div class="headline">System Information</div>
          </div>
        </v-card-title>
        <v-card-text>
          <div class="pb-3">
            <v-subheader>Error Details:</v-subheader>
            <span class="grey--text">There is a commission amount without an account name.</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" text @click.native="showCommDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <warning v-model="promptMsg" :label="errMsg" :showAcceptBtn="isWarning" @ok="acceptInterWarn = true"/>
  </div>
</template>

<script>
import MessageMixin from '@/mixins/message'
import { expandURI } from '@/utils/ajax'
import { formatPrice } from '@/utils/index'
import { formatYesNo, formatBoolean } from '@/utils/boolean'
// import { formatDisplayDate2 } from '@/utils/date'
import { POLICY_LIFECYCLE, POLICY_RISK_TAB, POLICYGENERAL_LOAD_DATA } from '@/store/types'
import { removeFormatNumber } from '@/utils/number'
import clone from 'clone'
import { isEqual } from '@/utils/compare'
import { commonAjaxMixin } from '@/mixins/common/commonAjaxMixin'
export default {
  data() {
    return {
      labels: {
        mainTitle: 'Risk Trailer',
        edit: 'Edit',
        riskItem: '',
        acctNo1: 'Billing A/C',
        acctNoSfx: 'Suffix',
        billCurr: 'Billing Currency',
        nap: 'Net Annual Premium (NAP)',
        premDue: 'Premium Due',
        applyGst: 'Apply GST',
        discPct: 'Discount %',
        disc: 'Discount',
        premGst: 'GST Premium',
        redBal1: 'Reducing Balance',
        comm1pct: 'Commission %',
        comm1: 'Commission',
        // comm1gst: 'GST Commission',
        stamp: 'Risk Stamp',
        fee: 'Risk Fee',
        tax: 'Risk Tax',
        polStamp: 'Policy Stamp',
        polFee: 'Policy Fee',
        polTax: 'Policy Tax',
        totAcctNo1: 'Total After Account(1)',
        save: 'Save',
        otherBilling: 'Other Billing A/Cs',
        coRiSummary: 'CO/RI Summary',
        acctNo2: 'Account 2',
        acctNo3: 'Account 3',
        redBal2: 'Reducing Balance',
        redBal3: 'Reducing Balance',
        comm2pct: 'Commission %',
        comm2: 'Commission',
        // comm2gst: 'GST Commission',
        totAcctNo2: 'Total After Account(2)',
        comm3pct: 'Commission %',
        comm3: 'Commission',
        // comm3gst: 'GST Commission',
        totAcctNo3: 'Total After Account(3)',
        si: 'Sum Insured',
        sharePct: 'Share %',
        coIns: 'Coinsurance',
        gross: 'Gross',
        stats: 'Statutory',
        fac: 'Facultative',
        surplus: 'Surplus',
        net: 'Net',
        qs: 'Quota Share',
        exit: 'Exit',
        proceed: 'Proceed'
        // applyGST: 'Apply GST'
      },
      loading: false,
      policy: {
        loading: false,
        saving: false
      },
      headers: [
        { text: 'Risk', align: 'left', value: 'riskItem' },
        { text: 'Class', value: 'busCls' },
        { text: 'NAP', value: 'dNAP' },
        { text: 'PD', value: 'premDue' },
        { text: 'Disc', value: 'disc' },
        { text: 'Comm1', value: 'comm1' },
        { text: 'Comm2', value: 'comm2' },
        { text: 'Comm3', value: 'comm3' },
        { text: 'R.Stamp', value: 'stamp' },
        { text: 'R.Fee', value: 'fee' },
        { text: 'R.Tax', value: 'tax' },
        { text: 'Apply GST', value: 'applyGst' },
        { text: 'Actions', value: 'action', align: 'center' }
      ],
      itemList: [],
      showExpandRiskTrailer: true,
      mainForm: {
        polNo: undefined,
        renCnt: undefined,
        endtCnt: undefined,
        riskGrpNo: undefined,
        riskNo: undefined,
        acctNo1: undefined,
        acctNoSfx: undefined,
        billCurr: undefined,
        nap: undefined,
        premDue: undefined,
        applyGst: undefined,
        discPctAe: undefined,
        discPct: undefined,
        discAe: undefined,
        disc: undefined,
        premGst: undefined,
        redBal1: undefined,
        comm1pctAe: undefined,
        comm1pct: undefined,
        comm1ae: undefined,
        comm1: undefined,
        // comm1gst: undefined,
        stampAe: undefined,
        stamp: undefined,
        feeAe: undefined,
        fee: undefined,
        taxAe: undefined,
        tax: undefined,
        polStamp: undefined,
        polFee: undefined,
        polTax: undefined,
        totAcctNo1: undefined,
        dDisc: undefined,
        dComm1: undefined,
        dRiskStamp: undefined,
        dRiskFee: undefined,
        dRiskTax: undefined,
        acctNo2: undefined,
        acctNo2Sfx: undefined,
        redBal2: undefined,
        comm2pctAe: undefined,
        comm2pct: undefined,
        comm2ae: undefined,
        comm2: undefined,
        // comm2gst: undefined,
        totAcctNo2: undefined,
        acctNo3: undefined,
        acctNo3Sfx: undefined,
        redBal3: undefined,
        comm3pctAe: undefined,
        comm3pct: undefined,
        comm3ae: undefined,
        comm3: undefined,
        // comm3gst: undefined,
        totAcctNo3: undefined
        // applyGST: false
      },
      riskItem: undefined,
      yesnoList: [
        {
          label: 'Yes',
          value: true
        },
        {
          label: 'No',
          value: false
        }
      ],
      stampLoading: false,
      feeLoading: false,
      taxLoading: false,
      interList: [],
      interLoading: false,
      showErrorDialog: false,
      riForm: {
        dCoinsSI: undefined,
        dCoinsSIPct: undefined,
        dSI: undefined,
        dStatSI: undefined,
        dStatSIPct: undefined,
        dFacSI: undefined,
        dFacSIPct: undefined,
        dTtySI: undefined,
        dTtySIPct: undefined,
        dNetSI: undefined,
        dNetSIPct: undefined,
        dOthSI: undefined,
        dOthSIPct: undefined
      },
      coRiLoading: false,
      perRiskStp: false,
      perRiskFee: false,
      perRiskTax: false,
      showCommDialog: false,
      errorCommWithoutAcctNo: true
    }
  },
  computed: {
    polMaster() {
      return this.$store.state.underwriting.policy.polMaster
    },
    polRisk() {
      return this.$store.state.underwriting
    },
    selectedRiskGrpNo() {
      return this.$store.state.underwriting.policy.selectedRiskGrpNo
    },
    selectedRiskNo() {
      return this.$store.state.underwriting.policy.selectedRiskNo
    },
    getFirstTrailerList() {
      return this.itemList.filter((risk) => {
        return risk.riskGrpNoRiskNo === this.itemList[0].riskGrpNoRiskNo
      })
    },
    getSelectedTrailer() {
      return this.itemList.filter((risk) => {
        return risk.riskItem === this.riskItem
      })
    },
    policyRiskItem() {
      return this.$store.state.underwriting.policyRiskItemSettings
    }
  },
  watch: {
    displayErrorDialog: {
      handler: function (messages) {
        if (messages && messages.length > 0) {
          this.showErrorDialog = true
        }
      },
      deep: true
    }
  },
  methods: {
    async doInit() {
      this.policy.saving = true
      await Promise.all([this.$store.dispatch('commonsInit')])
      await Promise.all([this.$store.dispatch('policyRiskItemInit')])
      if (this.policyRiskItem.init === 1) {
        this.getInterList()
        await this.getRiskTrailerList()
        this.policy.saving = false
      } else {
        this.policy.saving = false
      }
    },
    async getRiskTrailerList() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.riskTrailerList
      if (apiUrl) {
        try {
          this.loading = true
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          if (result.data.policyDto) {
            const commitObject = {
              data: result.data.policyDto
            }
            this.$store.commit(POLICYGENERAL_LOAD_DATA, commitObject)
          }
          this.displayRiskTrailerList(result.data.riskTrailerList).then(() => {
            this.loading = false
            if (this.getFirstTrailerList.length > 0) {
              this.doSetRiskTrailer(this.getFirstTrailerList[0])
            }
          })
        } catch (err) {
          this.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async displayRiskTrailerList(result) {
      if (result) {
        this.itemList = []
        result.map((item) => {
          const convertItem = {
            riskItem: item.riskGrpNo.toString().padStart(2, '0') + '/' + item.riskNo.toString().padStart(3, '0'),
            riskGrpNo: item.riskGrpNo,
            riskNo: item.riskNo,
            dNAP: formatPrice(item.dNAP),
            dDiscPct: item.dDiscPct.toFixed(4),
            dComm1Pct: item.dComm1Pct.toFixed(4),
            dComm2Pct: item.dComm2Pct.toFixed(4),
            dComm3Pct: item.dComm3Pct.toFixed(4),
            busCls: item.busCls,
            premDue: formatPrice(item.premDue),
            disc: formatPrice(item.disc),
            comm1: formatPrice(item.comm1),
            comm2: formatPrice(item.comm2),
            comm3: formatPrice(item.comm3),
            stamp: formatPrice(item.stamp),
            fee: formatPrice(item.fee),
            tax: formatPrice(item.tax),
            applyGst: formatYesNo(item.applyGst),
            acctNo1Name: item.acctNo1Name,
            acctNoSfxDesc: item.acctNoSfxDesc,
            discPctAe: item.discPctAe,
            discPct: item.discPct.toFixed(4),
            discAe: item.discAe,
            premGst: formatPrice(item.premGst),
            comm1pctAe: item.comm1pctAe,
            comm1pct: item.comm1pct.toFixed(4),
            comm1ae: item.comm1ae,
            // comm1gst: formatPrice(item.comm1gst),
            stampAe: item.stampAe,
            feeAe: item.feeAe,
            taxAe: item.taxAe,
            acctNo2Name: item.acctNo2Name,
            acctNo2SfxDesc: item.acctNo2SfxDesc,
            comm2pctAe: item.comm2pctAe,
            comm2pct: item.comm2pct.toFixed(4),
            comm2ae: item.comm2ae,
            // comm2gst: formatPrice(item.comm2gst),
            acctNo3Name: item.acctNo3Name,
            acctNo3SfxDesc: item.acctNo3SfxDesc,
            comm3pctAe: item.comm3pctAe,
            comm3pct: item.comm3pct.toFixed(4),
            comm3ae: item.comm3ae,
            // comm3gst: formatPrice(item.comm3gst),
            premGstrate: item.premGstrate
            // comm1gstrate: item.comm1gstrate,
            // comm2gstrate: item.comm2gstrate,
            // comm3gstrate: item.comm3gstrate
          }
          this.itemList.push(convertItem)
        })
      } else {
        this.itemList = []
      }
    },
    doSelectItem(data) {
      this.showExpandRiskTrailer = true
      this.doSetRiskTrailer(data)
    },
    async doSetRiskTrailer(data) {
      this.riskItem = data.riskItem
      this.labels.riskItem = data.riskItem
      this.mainForm.riskGrpNo = data.riskGrpNo
      this.mainForm.riskNo = data.riskNo
      this.mainForm.acctNo1 = this.polMaster.acctNo1 + ' - ' + data.acctNo1Name
      this.mainForm.acctNoSfx = data.acctNoSfxDesc
      this.mainForm.billCurr = this.polMaster.billCurr + ' - ' + this.polMaster.billCurrRate.toFixed(6)
      this.mainForm.nap = data.dNAP
      this.mainForm.premDue = data.premDue
      this.mainForm.applyGst = data.applyGst
      this.mainForm.discPctAe = data.discPctAe
      if (this.mainForm.discPctAe === false) {
        this.mainForm.discPct = data.discPct
      } else {
        this.mainForm.discPct = data.dDiscPct
      }

      this.mainForm.discAe = data.discAe
      this.mainForm.disc = data.disc
      this.mainForm.premGst = data.premGst

      this.mainForm.redBal1 = this.polMaster.redBal1
      this.mainForm.comm1pctAe = data.comm1pctAe
      this.mainForm.comm1pct = data.comm1pct
      this.mainForm.comm1ae = data.comm1ae
      if (this.mainForm.comm1ae === true) {
        if (this.mainForm.redBal1 === true) {
          this.mainForm.comm1 = formatPrice(
            this.checkIfZeroValue((parseFloat(removeFormatNumber(this.mainForm.premDue)) + parseFloat(removeFormatNumber(this.mainForm.disc))) *
              (parseFloat(removeFormatNumber(this.mainForm.comm1pct)) / 100))
          )
        } else {
          this.mainForm.comm1 = formatPrice(
            this.checkIfZeroValue(((parseFloat(removeFormatNumber(this.mainForm.premDue)) * parseFloat(removeFormatNumber(this.mainForm.comm1pct))) / 100))
          )
        }
      } else {
        this.mainForm.comm1 = data.comm1
      }
      this.mainForm.stampAe = data.stampAe
      if (this.mainForm.stampAe === true) {
        await this.doRiskStamp()
      } else {
        this.mainForm.stamp = data.stamp
      }

      this.mainForm.feeAe = data.feeAe
      if (this.mainForm.feeAe === true) {
        await this.doRiskFee()
      } else {
        this.mainForm.fee = data.fee
      }
      this.mainForm.taxAe = data.taxAe
      if (this.mainForm.taxAe === true) {
        await this.doRiskTax()
      } else {
        this.mainForm.tax = data.tax
      }
      this.mainForm.polStamp = formatPrice(this.polMaster.polStamp)
      this.mainForm.polFee = formatPrice(this.polMaster.polFee)
      this.mainForm.polTax = formatPrice(this.polMaster.polTax)
      this.doTotAcctNo1()

      // Other Billing A/Cs
      this.mainForm.acctNo2 = this.polMaster.acctNo2
      if (data.acctNo2SfxDesc !== null && data.acctNo2SfxDesc !== undefined && data.acctNo2SfxDesc !== '') {
        this.mainForm.acctNo2Sfx = data.acctNo2SfxDesc
      } else {
        this.mainForm.acctNo2Sfx = ''
      }
      this.mainForm.redBal2 = this.polMaster.redBal2
      this.mainForm.comm2pctAe = data.comm2pctAe
      this.mainForm.comm2pct = data.comm2pct
      this.mainForm.comm2ae = data.comm2ae
      if (this.mainForm.comm2ae === true) {
        if (this.mainForm.redBal2 === true) {
          this.mainForm.comm2 = formatPrice(
            this.checkIfZeroValue((parseFloat(removeFormatNumber(this.mainForm.premDue)) +
              parseFloat(removeFormatNumber(this.mainForm.comm1)) +
              parseFloat(removeFormatNumber(this.mainForm.disc))) *
              (parseFloat(removeFormatNumber(this.mainForm.comm2pct)) / 100))
          )
        } else {
          this.mainForm.comm2 = formatPrice(
            this.checkIfZeroValue(((parseFloat(removeFormatNumber(this.mainForm.premDue)) * parseFloat(removeFormatNumber(this.mainForm.comm2pct))) / 100))
          )
        }
      } else {
        this.mainForm.comm2 = data.comm2
      }

      // this.mainForm.comm2gst = data.comm2gst
      this.doTotAcctNo2()

      this.mainForm.acctNo3 = this.polMaster.acctNo3
      if (data.acctNo3SfxDesc !== null && data.acctNo3SfxDesc !== undefined && data.acctNo3SfxDesc !== '') {
        this.mainForm.acctNo3Sfx = data.acctNo3SfxDesc
      } else {
        this.mainForm.acctNo3Sfx = ''
      }
      this.mainForm.redBal3 = this.polMaster.redBal3
      this.mainForm.comm3pctAe = data.comm3pctAe
      this.mainForm.comm3pct = data.comm3pct
      this.mainForm.comm3ae = data.comm3ae
      if (this.mainForm.comm3ae === true) {
        if (this.mainForm.redBal3 === true) {
          this.mainForm.comm3 = formatPrice(
            this.checkIfZeroValue((parseFloat(removeFormatNumber(this.mainForm.premDue)) +
              parseFloat(removeFormatNumber(this.mainForm.comm1)) +
              parseFloat(removeFormatNumber(this.mainForm.comm2)) +
              parseFloat(removeFormatNumber(this.mainForm.disc))) *
              (parseFloat(removeFormatNumber(this.mainForm.comm3pct)) / 100))
          )
        } else {
          this.mainForm.comm3 = formatPrice(
            this.checkIfZeroValue(((parseFloat(removeFormatNumber(this.mainForm.premDue)) * parseFloat(removeFormatNumber(this.mainForm.comm3pct))) / 100))
          )
        }
      } else {
        this.mainForm.comm3 = data.comm3
      }

      // this.mainForm.comm3gst = data.comm3gst
      this.doTotAcctNo3()

      this.getCoRiSummary()
      // AR: Moved 17-Sep OS-440
      this.getBranchDRI()
      this.copymainForm = clone(this.mainForm)
      // 2020-12-22 LYT Fixed on blur to Zero
      // this.$refs.discPct.$el.cleave.setRawValue(this.mainForm.discPct)
      // this.$refs.disc.$el.cleave.setRawValue(this.mainForm.disc)
      // this.$refs.comm1pct.$el.cleave.setRawValue(this.mainForm.comm1pct)
      // this.$refs.comm1.$el.cleave.setRawValue(this.mainForm.comm1)
      // this.$refs.stamp.$el.cleave.setRawValue(this.mainForm.stamp)
      // this.$refs.fee.$el.cleave.setRawValue(this.mainForm.fee)
      // this.$refs.tax.$el.cleave.setRawValue(this.mainForm.tax)
      // this.$refs.comm2pct.$el.cleave.setRawValue(this.mainForm.comm2pct)
      // this.$refs.comm2.$el.cleave.setRawValue(this.mainForm.comm2)
      // this.$refs.comm3pct.$el.cleave.setRawValue(this.mainForm.comm3pct)
      // this.$refs.comm3.$el.cleave.setRawValue(this.mainForm.comm3)
    },
    doDiscPct() {
      if (this.mainForm.discPctAe === false) {
        this.mainForm.discPct = this.getSelectedTrailer[0].discPct
      } else {
        this.mainForm.discPct = this.getSelectedTrailer[0].dDiscPct
      }
      this.doDisc()
    },
    doDisc() {
      if (this.mainForm.discAe === false) {
        this.mainForm.disc = this.getSelectedTrailer[0].disc
      } else {
        // • ( [polRisk.premDue] * [polRiskk.discPct] / 100 ) * -1
        this.mainForm.disc = formatPrice(
          this.checkIfZeroValue(((parseFloat(removeFormatNumber(this.getSelectedTrailer[0].premDue)) * parseFloat(removeFormatNumber(this.mainForm.discPct))) / 100))
        )
      }
      this.doPremGST()
    },
    doPremGST() {
      if (this.mainForm.applyGst === 'NO') {
        this.mainForm.premGst = formatPrice(0)
      } else {
        // 2020-10-26 LYT OS-508
        // this.mainForm.premGst = formatPrice((parseFloat(removeFormatNumber(this.mainForm.premDue)) + parseFloat(removeFormatNumber(this.mainForm.disc))) *
        // parseFloat(removeFormatNumber(this.getSelectedTrailer[0].premGstrate)) / 100)
        this.mainForm.premGst = formatPrice(
          ((parseFloat(removeFormatNumber(this.mainForm.premDue)) + parseFloat(removeFormatNumber(this.mainForm.disc))) *
            parseFloat(removeFormatNumber(this.polMaster.gstRatePrem))) /
            100
        )
      }
      this.doComm1()
      this.doTotAcctNo1()
    },
    doComm1pctAe() {
      if (this.mainForm.comm1pctAe === false) {
        this.mainForm.comm1pct = this.getSelectedTrailer[0].comm1pct
      } else {
        this.mainForm.comm1pct = this.getSelectedTrailer[0].dComm1Pct
      }
      this.doComm1()
    },
    checkIfZeroValue(value) {
      if (value !== 0) {
        return value * -1
      }
      return 0
    },
    doComm1() {
      if (this.mainForm.comm1ae === true) {
        if (this.mainForm.redBal1 === true) {
          this.mainForm.comm1 = formatPrice(
            this.checkIfZeroValue((parseFloat(removeFormatNumber(this.mainForm.premDue)) + parseFloat(removeFormatNumber(this.mainForm.disc))) *
            (parseFloat(removeFormatNumber(this.mainForm.comm1pct)) / 100))
          )
        } else {
          this.mainForm.comm1 = formatPrice(
            this.checkIfZeroValue(((parseFloat(removeFormatNumber(this.mainForm.premDue)) * parseFloat(removeFormatNumber(this.mainForm.comm1pct))) / 100))
          )
        }
      } else {
        this.mainForm.comm1 = this.getSelectedTrailer[0].comm1
      }
      // 2020-06-03 LYT remarked based on revised spec on 08May2020
      // this.doComm1GST()
      this.doTotAcctNo1()
    },
    // 2020-06-03 LYT remarked based on revised spec on 08May2020
    // doComm1GST() {
    //   this.mainForm.comm1gst = formatPrice(parseFloat(removeFormatNumber(this.mainForm.comm1)) * parseFloat(removeFormatNumber(this.getSelectedTrailer[0].comm1gstrate)) / 100)
    //   this.doTotAcctNo1()
    // },
    async doStampAe() {
      if (this.mainForm.stampAe === true) {
        await this.doRiskStamp()
      } else {
        this.mainForm.stamp = this.getSelectedTrailer[0].stamp
      }
      this.doTotAcctNo1()
    },
    async doRiskStamp() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.getRiskStamp
      if (apiUrl) {
        try {
          this.stampLoading = true
          var dEffDate = new Date(this.polMaster.tranDate)
          if (this.polMaster.comDate !== undefined && this.polMaster.comDate !== null) {
            dEffDate = new Date(this.polMaster.comDate)
          }
          const expand = {
            branch: this.polMaster.branch,
            dRi: this.polMaster.dri,
            effDate: dEffDate,
            premDue: removeFormatNumber(this.mainForm.premDue),
            disc: removeFormatNumber(this.mainForm.disc)
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))

          if (result.data) {
            const { data } = result
            this.mainForm.stamp = formatPrice(data.data)
          }
          this.stampLoading = false
        } catch (err) {
          this.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async doFeeAe() {
      if (this.mainForm.feeAe === true) {
        await this.doRiskFee()
      } else {
        this.mainForm.fee = this.getSelectedTrailer[0].fee
      }
      this.doTotAcctNo1()
    },
    async doRiskFee() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.getRiskFee
      if (apiUrl) {
        try {
          this.feeLoading = true
          var dEffDate = new Date(this.polMaster.tranDate)
          if (this.polMaster.comDate !== undefined && this.polMaster.comDate !== null) {
            dEffDate = new Date(this.polMaster.comDate)
          }
          const expand = {
            branch: this.polMaster.branch,
            dRi: this.polMaster.dri,
            effDate: dEffDate,
            premDue: removeFormatNumber(this.mainForm.premDue),
            disc: removeFormatNumber(this.mainForm.disc)
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))

          if (result.data) {
            const { data } = result
            this.mainForm.fee = formatPrice(data.data)
          }
          this.feeLoading = false
        } catch (err) {
          this.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async doTaxAe() {
      if (this.mainForm.taxAe === true) {
        await this.doRiskTax()
      } else {
        this.mainForm.tax = this.getSelectedTrailer[0].tax
      }
      this.doTotAcctNo1()
    },
    async doRiskTax() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.getRiskTax
      if (apiUrl) {
        try {
          this.taxLoading = true
          var dEffDate = new Date(this.polMaster.tranDate)
          if (this.polMaster.comDate !== undefined && this.polMaster.comDate !== null) {
            dEffDate = new Date(this.polMaster.comDate)
          }
          const expand = {
            branch: this.polMaster.branch,
            dRi: this.polMaster.dri,
            effDate: dEffDate,
            premDue: removeFormatNumber(this.mainForm.premDue),
            disc: removeFormatNumber(this.mainForm.disc)
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))

          if (result.data) {
            const { data } = result
            this.mainForm.tax = formatPrice(data.data)
          }
          this.taxLoading = false
        } catch (err) {
          this.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    doTotAcctNo1() {
      this.mainForm.totAcctNo1 =
        parseFloat(removeFormatNumber(this.polMaster.polPd)) +
        parseFloat(removeFormatNumber(this.polMaster.polStamp)) +
        parseFloat(removeFormatNumber(this.polMaster.polFee)) +
        parseFloat(removeFormatNumber(this.polMaster.polTax)) +
        parseFloat(removeFormatNumber(this.polMaster.comm1)) +
        parseFloat(removeFormatNumber(this.mainForm.comm1)) -
        parseFloat(removeFormatNumber(this.getSelectedTrailer[0].comm1)) +
        parseFloat(removeFormatNumber(this.polMaster.disc)) +
        parseFloat(removeFormatNumber(this.mainForm.disc)) -
        parseFloat(removeFormatNumber(this.getSelectedTrailer[0].disc)) +
        parseFloat(removeFormatNumber(this.polMaster.riskStamp)) +
        parseFloat(removeFormatNumber(this.mainForm.stamp)) -
        parseFloat(removeFormatNumber(this.getSelectedTrailer[0].stamp)) +
        parseFloat(removeFormatNumber(this.polMaster.riskFee)) +
        parseFloat(removeFormatNumber(this.mainForm.fee)) -
        parseFloat(removeFormatNumber(this.getSelectedTrailer[0].fee)) +
        parseFloat(removeFormatNumber(this.polMaster.riskTax)) +
        parseFloat(removeFormatNumber(this.mainForm.tax)) -
        parseFloat(removeFormatNumber(this.getSelectedTrailer[0].tax))
      this.mainForm.totAcctNo1 = formatPrice(this.mainForm.totAcctNo1)
      this.mainForm.dDisc = parseFloat(this.getSelectedTrailer[0].disc)
      this.mainForm.dComm1 = parseFloat(this.getSelectedTrailer[0].comm1)
      this.mainForm.dRiskStamp = parseFloat(this.getSelectedTrailer[0].stamp)
      this.mainForm.dRiskFee = parseFloat(this.getSelectedTrailer[0].fee)
      this.mainForm.dRiskTax = parseFloat(this.getSelectedTrailer[0].tax)
      this.doComm2pctAe()
      this.doTotAcctNo2()
    },
    async doSaveRiskTrailer() {
      this.errorCommWithoutAcctNo = true
      if (this.mainForm.acctNo1 === null) {
        this.mainForm.acctNo1 = ''
      }
      if (this.mainForm.acctNo2 === null) {
        this.mainForm.acctNo2 = ''
      }
      if (this.mainForm.acctNo3 === null) {
        this.mainForm.acctNo3 = ''
      }
      if ((this.mainForm.comm1pct !== '0.0000' && this.mainForm.acctNo1 === '') ||
      (this.mainForm.comm2pct !== '0.0000' && this.mainForm.acctNo2 === '') ||
      (this.mainForm.comm3pct !== '0.0000' && this.mainForm.acctNo3 === '')) {
        this.showCommDialog = true
        this.errorCommWithoutAcctNo = false
      }
      if (this.errorCommWithoutAcctNo) {
        this.resetMessages()
        this.$validator.validateAll().then((result) => {
          if (result) {
            this.policy.saving = true
            this.saveRiskTrailers().then(success => {
              if (success) {
                this.doInit()
                this.$toast.success('Risk Trailer Successfully Saved')
                this.policy.saving = false
              } else {
                this.$toast.error('Risk Trailer Save Failed')
                this.policy.saving = false
              }
            })
          } else {
            this.promptFieldCheck()
          }
        })
      }
    },
    insertInter(...inter) {
      let acctNos = []
      for (let item of inter) {
        if (item !== null && item !== undefined && item !== '') {
          acctNos.push(item)
        }
      }
      return acctNos
    },
    async saveRiskTrailers() {
      let acctNos = this.insertInter(this.mainForm.acctNo2, this.mainForm.acctNo3)
      let result = false
      if (acctNos.length === 0) {
        await this.saveRiskTrailer().then(success => {
          result = success
        })
      } else {
        await this.validateIntermediary(this.polMaster.tranDate, acctNos.join(',')).then(async () => {
          if (this.promptMsg === false || this.acceptInterWarn === true) {
            await this.saveRiskTrailer().then(success => {
              result = success
            })
          }
        })
      }
      return result
    },
    async saveRiskTrailer() {
      try {
        this.mainForm.polNo = this.polMaster.polNo
        this.mainForm.renCnt = this.polMaster.renCnt
        this.mainForm.endtCnt = this.polMaster.endtCnt
        this.copymainForm.polNo = this.polMaster.polNo
        this.copymainForm.renCnt = this.polMaster.renCnt
        this.copymainForm.endtCnt = this.polMaster.endtCnt

        if (this.mainForm.acctNo2 === undefined) {
          this.mainForm.acctNo2 = ''
        }
        if (this.mainForm.acctNo3 === undefined) {
          this.mainForm.acctNo3 = ''
        }
        this.mainForm.nap = removeFormatNumber(this.mainForm.nap)
        this.mainForm.premDue = removeFormatNumber(this.mainForm.premDue)
        this.mainForm.discPct = removeFormatNumber(this.mainForm.discPct)
        this.mainForm.disc = removeFormatNumber(this.mainForm.disc)
        this.mainForm.premGst = removeFormatNumber(this.mainForm.premGst)
        this.mainForm.comm1pct = removeFormatNumber(this.mainForm.comm1pct)
        this.mainForm.comm1 = removeFormatNumber(this.mainForm.comm1)
        this.mainForm.stamp = removeFormatNumber(this.mainForm.stamp)
        this.mainForm.fee = removeFormatNumber(this.mainForm.fee)
        this.mainForm.tax = removeFormatNumber(this.mainForm.tax)
        this.mainForm.polStamp = removeFormatNumber(this.mainForm.polStamp)
        this.mainForm.polFee = removeFormatNumber(this.mainForm.polFee)
        this.mainForm.polTax = removeFormatNumber(this.mainForm.polTax)
        this.mainForm.totAcctNo1 = removeFormatNumber(this.mainForm.totAcctNo1)
        this.mainForm.dDisc = removeFormatNumber(this.mainForm.dDisc)
        this.mainForm.dComm1 = removeFormatNumber(this.mainForm.dComm1)
        this.mainForm.dRiskStamp = removeFormatNumber(this.mainForm.dRiskStamp)
        this.mainForm.dRiskFee = removeFormatNumber(this.mainForm.dRiskFee)
        this.mainForm.dRiskTax = removeFormatNumber(this.mainForm.dRiskTax)
        this.mainForm.comm2pct = removeFormatNumber(this.mainForm.comm2pct)
        this.mainForm.comm2 = removeFormatNumber(this.mainForm.comm2)
        this.mainForm.totAcctNo2 = removeFormatNumber(this.mainForm.totAcctNo2)
        this.mainForm.comm3pct = removeFormatNumber(this.mainForm.comm3pct)
        this.mainForm.comm3 = removeFormatNumber(this.mainForm.comm3)
        this.mainForm.totAcctNo3 = removeFormatNumber(this.mainForm.totAcctNo3)
        this.mainForm.applyGst = formatBoolean(this.mainForm.applyGst)

        this.copymainForm.nap = removeFormatNumber(this.copymainForm.nap)
        this.copymainForm.premDue = removeFormatNumber(this.copymainForm.premDue)
        this.copymainForm.discPct = removeFormatNumber(this.copymainForm.discPct)
        this.copymainForm.disc = removeFormatNumber(this.copymainForm.disc)
        this.copymainForm.premGst = removeFormatNumber(this.copymainForm.premGst)
        this.copymainForm.comm1pct = removeFormatNumber(this.copymainForm.comm1pct)
        this.copymainForm.comm1 = removeFormatNumber(this.copymainForm.comm1)
        this.copymainForm.stamp = removeFormatNumber(this.copymainForm.stamp)
        this.copymainForm.fee = removeFormatNumber(this.copymainForm.fee)
        this.copymainForm.tax = removeFormatNumber(this.copymainForm.tax)
        this.copymainForm.polStamp = removeFormatNumber(this.copymainForm.polStamp)
        this.copymainForm.polFee = removeFormatNumber(this.copymainForm.polFee)
        this.copymainForm.polTax = removeFormatNumber(this.copymainForm.polTax)
        this.copymainForm.totAcctNo1 = removeFormatNumber(this.copymainForm.totAcctNo1)
        this.copymainForm.dDisc = removeFormatNumber(this.copymainForm.dDisc)
        this.copymainForm.dComm1 = removeFormatNumber(this.copymainForm.dComm1)
        this.copymainForm.dRiskStamp = removeFormatNumber(this.copymainForm.dRiskStamp)
        this.copymainForm.dRiskFee = removeFormatNumber(this.copymainForm.dRiskFee)
        this.copymainForm.dRiskTax = removeFormatNumber(this.copymainForm.dRiskTax)
        this.copymainForm.comm2pct = removeFormatNumber(this.copymainForm.comm2pct)
        this.copymainForm.comm2 = removeFormatNumber(this.copymainForm.comm2)
        this.copymainForm.totAcctNo2 = removeFormatNumber(this.copymainForm.totAcctNo2)
        this.copymainForm.comm3pct = removeFormatNumber(this.copymainForm.comm3pct)
        this.copymainForm.comm3 = removeFormatNumber(this.copymainForm.comm3)
        this.copymainForm.totAcctNo3 = removeFormatNumber(this.copymainForm.totAcctNo3)
        this.copymainForm.applyGst = formatBoolean(this.copymainForm.applyGst)
        if (isEqual(this.mainForm, this.copymainForm) === true) {
          return true
        } else {
          const result = await this.$store.dispatch('saveRiskTrailer', this.mainForm)
          if (result) {
            this.copymainForm = clone(this.mainForm)
            return true
          } else {
            return false
          }
        }
      } catch (err) {
        return false
      }
    },
    async getInterList() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.interList
      if (apiUrl) {
        try {
          this.interLoading = true
          const expand = {
            acctNo1: this.polMaster.acctNo1,
            polType: this.polMaster.polType
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))

          if (result.data) {
            const { data } = result.data
            if (data) {
              this.$set(this, 'interList', data)
            }
          }
          this.interLoading = false
        } catch (err) {
          this.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    doComm2pctAe(newValue) {
      if (this.mainForm.comm2pctAe === false) {
        // this.mainForm.comm2pct = this.getSelectedTrailer[0].comm2pct
      } else {
        const interFiler = this.interList.filter((list) => {
          return list.value === this.mainForm.acctNo2
        })
        if (interFiler.length === 0) {
          this.mainForm.comm2pct = 0.0000
          this.mainForm.comm2pct = this.mainForm.comm2pct.toFixed(4)
        } else {
          this.mainForm.comm2pct = interFiler[0].secondaryValue.toFixed(4)
        }
      }
      this.doComm2()
    },
    changeAcctNoComm2pctAe(newValue) {
      if (this.mainForm.comm2pctAe === false) {
        // this.mainForm.comm2pct = this.getSelectedTrailer[0].comm2pct
      } else {
        const interFiler = this.interList.filter((list) => {
          return list.value === newValue
        })
        if (interFiler.length === 0) {
          this.mainForm.comm2pct = 0.0000
          this.mainForm.comm2pct = this.mainForm.comm2pct.toFixed(4)
        } else {
          this.mainForm.comm2pct = interFiler[0].secondaryValue.toFixed(4)
        }
        // this.mainForm.comm2pct = this.getSelectedTrailer[0].dComm2Pct
      }
      this.doComm2()
    },
    changeAcctNoComm3pctAe(newValue) {
      if (this.mainForm.comm3pctAe === false) {
        // this.mainForm.comm3pct = this.getSelectedTrailer[0].comm3pct
      } else {
        const interFiler = this.interList.filter((list) => {
          return list.value === newValue
        })
        if (interFiler.length === 0) {
          this.mainForm.comm3pct = 0.0000
          this.mainForm.comm3pct = this.mainForm.comm3pct.toFixed(4)
        } else {
          this.mainForm.comm3pct = interFiler[0].secondaryValue.toFixed(4)
        }
        // this.mainForm.comm2pct = this.getSelectedTrailer[0].dComm2Pct
      }
      this.doComm3()
    },
    onValueChange(newValue) {
      this.changeAcctNoComm2pctAe(newValue)
    },
    onValueChangeComm3(newValue) {
      this.changeAcctNoComm3pctAe(newValue)
    },
    doComm2() {
      if (this.mainForm.comm2ae === true) {
        if (this.mainForm.redBal2 === true) {
          this.mainForm.comm2 = formatPrice(
            this.checkIfZeroValue((parseFloat(removeFormatNumber(this.mainForm.premDue)) +
              parseFloat(removeFormatNumber(this.mainForm.comm1)) +
              parseFloat(removeFormatNumber(this.mainForm.disc))) *
              (parseFloat(removeFormatNumber(this.mainForm.comm2pct)) / 100))
          )
        } else {
          this.mainForm.comm2 = formatPrice(
            this.checkIfZeroValue((parseFloat(removeFormatNumber(this.mainForm.premDue)) * parseFloat(removeFormatNumber(this.mainForm.comm2pct))) / 100)
          )
        }
      } else {
        this.mainForm.comm2 = this.getSelectedTrailer[0].comm2
      }
      // this.doComm2GST()
      this.doTotAcctNo2()
    },
    // doComm2GST() {
    //   // this.mainForm.comm2gst = formatPrice(parseFloat(this.mainForm.comm2) * parseFloat(this.polMaster.gstRateComm2) / 100)
    //   this.mainForm.comm2gst = formatPrice(parseFloat(removeFormatNumber(this.mainForm.comm2)) * parseFloat(this.getSelectedTrailer[0].comm2gstrate) / 100)
    //   this.doTotAcctNo2()
    // },
    doTotAcctNo2() {
      // Derived as:
      // Total After Account(1) + [polMaster.comm2] + [riskComm.comm2] – dComm2
      // Note:
      // i. [riskComm.comm2] is screen value;
      // ii. dComm2 is on DB (i.e. initial values before update)
      this.mainForm.totAcctNo2 =
        parseFloat(removeFormatNumber(this.mainForm.totAcctNo1)) +
        parseFloat(removeFormatNumber(this.polMaster.comm2)) +
        parseFloat(removeFormatNumber(this.mainForm.comm2)) -
        parseFloat(removeFormatNumber(this.getSelectedTrailer[0].comm2))
      this.mainForm.totAcctNo2 = formatPrice(this.mainForm.totAcctNo2)
      this.doComm3pctAe()
      this.doTotAcctNo3()
    },
    doComm3pctAe() {
      if (this.mainForm.comm3pctAe === false) {
        // this.mainForm.comm3pct = this.getSelectedTrailer[0].comm3pct
      } else {
        const interFiler = this.interList.filter((list) => {
          return list.value === this.mainForm.acctNo3
        })
        if (interFiler.length === 0) {
          this.mainForm.comm3pct = 0.0000
          this.mainForm.comm3pct = this.mainForm.comm3pct.toFixed(4)
        } else {
          this.mainForm.comm3pct = interFiler[0].secondaryValue.toFixed(4)
        }
      }
      this.doComm3()
    },
    doComm3() {
      if (this.mainForm.comm3ae === true) {
        if (this.mainForm.redBal3 === true) {
          this.mainForm.comm3 = formatPrice(
            this.checkIfZeroValue((parseFloat(removeFormatNumber(this.mainForm.premDue)) +
              parseFloat(removeFormatNumber(this.mainForm.comm1)) +
              parseFloat(removeFormatNumber(this.mainForm.comm2)) +
              parseFloat(removeFormatNumber(this.mainForm.disc))) *
              (parseFloat(removeFormatNumber(this.mainForm.comm3pct)) / 100))
          )
        } else {
          this.mainForm.comm3 = formatPrice(
            this.checkIfZeroValue((parseFloat(removeFormatNumber(this.mainForm.premDue)) * parseFloat(removeFormatNumber(this.mainForm.comm3pct))) / 100)
          )
        }
      } else {
        this.mainForm.comm3 = this.getSelectedTrailer[0].comm3
      }
      // this.doComm3GST()
      this.doTotAcctNo3()
    },
    // doComm3GST() {
    //   // this.mainForm.comm3gst = formatPrice(parseFloat(this.mainForm.comm3) * parseFloat(this.polMaster.gstRateComm3) / 100)
    //   this.mainForm.comm3gst = formatPrice(parseFloat(removeFormatNumber(this.mainForm.comm3)) * parseFloat(this.getSelectedTrailer[0].comm3gstrate) / 100)
    //   this.doTotAcctNo3()
    // },
    doTotAcctNo3() {
      // Derived as:
      // Total After Account(2) + [polMaster.comm3] + [riskComm.comm3] – dComm3
      // Note:
      // i. [riskComm.comm3] is screen value;
      // ii. dComm3 is on DB (i.e. initial values before update)
      this.mainForm.totAcctNo3 =
        parseFloat(removeFormatNumber(this.mainForm.totAcctNo2)) +
        parseFloat(removeFormatNumber(this.polMaster.comm3)) +
        parseFloat(removeFormatNumber(this.mainForm.comm3)) -
        parseFloat(removeFormatNumber(this.getSelectedTrailer[0].comm3))
      this.mainForm.totAcctNo3 = formatPrice(this.mainForm.totAcctNo3)
    },
    async getCoRiSummary() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.coRiSummary
      if (apiUrl) {
        try {
          this.coRiLoading = true
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt,
            riskGrpNo: this.mainForm.riskGrpNo,
            riskNo: this.mainForm.riskNo
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))

          if (result.data) {
            const { data } = result
            this.riForm.dCoinsSI = formatPrice(data.dCoinsSI)
            this.riForm.dCoinsSIPct = data.dCoinsSIPct.toFixed(6)
            this.riForm.dSI = formatPrice(data.dSI)
            this.riForm.dStatSI = formatPrice(data.dStatSI)
            this.riForm.dStatSIPct = data.dStatSIPct.toFixed(6)
            this.riForm.dFacSI = formatPrice(data.dFacSI)
            this.riForm.dFacSIPct = data.dFacSIPct.toFixed(6)
            this.riForm.dTtySI = formatPrice(data.dTtySI)
            this.riForm.dTtySIPct = data.dTtySIPct.toFixed(6)
            this.riForm.dNetSI = formatPrice(data.dNetSI)
            this.riForm.dNetSIPct = data.dNetSIPct.toFixed(6)
            this.riForm.dOthSI = formatPrice(data.dOthSI)
            this.riForm.dOthSIPct = data.dOthSIPct.toFixed(6)
          }
          this.coRiLoading = false
        } catch (err) {
          this.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    doExit() {
      this.$router.push({ name: 'policyListing' })
    },
    async doProceed() {
      this.resetMessages()
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.policy.saving = true
          this.saveRiskTrailers().then(success => {
            if (success) {
              this.$store.commit(POLICY_RISK_TAB, 0)
              this.$store.commit(POLICY_LIFECYCLE, 4)
              this.$router.push({ name: 'policySummary' })
              // this.$toast.success('Risk Trailer Successfully Saved')
            } else {
              this.$toast.error('Risk Trailer Save Failed')
            }
            this.policy.saving = false
          })
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async getBranchDRI() {
      const apiUrl = this.policyRiskItem.menu.branchDRI
      if (apiUrl) {
        try {
          const expand = {
            branch: this.polMaster.branch,
            dri: this.polMaster.dri
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result.data
          if (data) {
            this.perRiskStp = data.perRiskStp
            this.perRiskFee = data.perRiskFee
            this.perRiskTax = data.perRiskTax
          }
        } catch (err) {

        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.[getBranchDRI]')
      }
    }
  },
  mounted() {
    this.doInit()
  },
  mixins: [MessageMixin, commonAjaxMixin]
}
</script>

<style>
</style>
